import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PageTitle, Button, Table } from '@cordata/buffet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from 'reactstrap';
import { toast } from 'react-toastify';

import useSlice from '~/utils/slice/hook';
import PartialSlicePicker from '~/components/SituationTables/PartialSlicePicker';
import StudyFlagForm from '~/components/SituationTables/StudyFlags/Form';
import { fetchStudyFlags, deleteStudyFlag, reactivateStudyFlag, fetchStudyFlag } from '../../../utils/situation-tables/study-flags-api';

const VALUE_TYPE_LABELS = {
  boolean: 'True or False',
  text: 'Text',
  number: 'Number',
  single_select: 'Single Select',
  multi_select: 'Multi Select',
  date: 'Date',
};

const SCOPE_LABELS = {
  global: 'System',
  organization: 'Organization',
  division: 'Division',
};

const ValueTypeCell = ({ value }) => VALUE_TYPE_LABELS[value] || value;
ValueTypeCell.propTypes = {
  value: PropTypes.string,
};
ValueTypeCell.defaultProps = {
  value: '',
};

const ScopeCell = ({ value }) => SCOPE_LABELS[value] || value;
ScopeCell.propTypes = {
  value: PropTypes.string,
};
ScopeCell.defaultProps = {
  value: '',
};

const StatusCell = ({ value }) => (
  <Badge color={value ? 'secondary' : 'success'}>
    {value ? 'Inactive' : 'Active'}
  </Badge>
);
StatusCell.propTypes = {
  value: PropTypes.string,
};
StatusCell.defaultProps = {
  value: null,
};

const ActionsCell = ({ original, onDisable, onReactivate }) => (
  <div
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
      }
    }}
    role="button"
    tabIndex={0}
  >
    {original.discarded_at ? (
      <Button
        size="sm"
        color="success"
        outline
        onClick={(e) => onReactivate(original.id, e)}
      >
        Reactivate
      </Button>
    ) : (
      <Button
        size="sm"
        color="danger"
        outline
        onClick={(e) => onDisable(original.id, e)}
      >
        Disable
      </Button>
    )}
  </div>
);
ActionsCell.propTypes = {
  original: PropTypes.shape({
    id: PropTypes.string,
    discarded_at: PropTypes.string,
  }).isRequired,
  onDisable: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
};

const StudyFlags = () => {
  const [studyFlags, setStudyFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const {
    fetchAll,
    setCurrentSlice,
    currentSlice,
  } = useSlice();

  useEffect(() => {
    fetchAll(['organization', 'division']);
    setCurrentSlice('all');
  }, []);

  useEffect(() => {
    setLoading(true);

    const params = {
      withDiscarded: true,
      sliceType: currentSlice.type || 'all',
    };

    if (currentSlice.type === 'organization' && currentSlice.id) {
      params.sliceId = currentSlice.id;
    } else if (currentSlice.type === 'division' && currentSlice.id) {
      params.sliceId = currentSlice.id;
    }

    fetchStudyFlags(params)
      .then((response) => {
        setStudyFlags(response.data || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching study flags:', error);
        setLoading(false);
      });
  }, [refreshKey, currentSlice]);

  const handleDisable = useCallback((id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to disable this study flag?')) {
      deleteStudyFlag(id)
        .then(() => setRefreshKey(prev => prev + 1));
    }
  }, []);

  const handleReactivate = useCallback((id, e) => {
    e.stopPropagation();
    reactivateStudyFlag(id)
      .then(() => setRefreshKey(prev => prev + 1));
  }, []);

  const tableColumns = useMemo(() => [{
    id: 'questionText',
    Header: 'Question',
    accessor: 'question_text',
  }, {
    id: 'valueType',
    Header: 'Value Type',
    accessor: 'value_type',
    Cell: ValueTypeCell,
  }, {
    id: 'scope',
    Header: 'Scope',
    accessor: 'context_level',
    Cell: ScopeCell,
  }, {
    id: 'status',
    Header: 'Status',
    accessor: 'discarded_at',
    Cell: StatusCell,
  }, {
    id: 'actions',
    Header: 'Actions',
    filterable: false,
    sortable: false,
    Cell: (props) => (
      <ActionsCell
        {...props}
        onDisable={handleDisable}
        onReactivate={handleReactivate}
      />
    ),
  }], [handleDisable, handleReactivate]);

  const defaultFilterMethod = useCallback(
    (filter, row) =>
      String(row[filter.id] || '').toLowerCase().includes(filter.value.toLowerCase())
    , [],
  );

  useEffect(() => {
    if (selectedId === 'add') {
      setFormData({});
      setFormLoading(false);
    } else if (selectedId) {
      setFormLoading(true);
      fetchStudyFlag(selectedId)
        .then((response) => {
          setFormData(response.data);
          setFormLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching study flag:', error);
          toast.error('Failed to load study flag');
          setSelectedId(null);
          setFormLoading(false);
        });
    }
  }, [selectedId]);

  const handleFormSuccess = useCallback(() => {
    setSelectedId(null);
    setFormData(null);
    setRefreshKey(prev => prev + 1);
  }, []);

  const handleFormCancel = useCallback(() => {
    setSelectedId(null);
    setFormData(null);
  }, []);

  const getTdProps = useCallback((state, rowInfo, column) => ({
    onClick: () => {
      if (column.id !== 'actions') {
        setSelectedId(rowInfo.original.id);
      }
    },
  }), []);

  return (
    <>
      <PageTitle
        action={
          <Button
            outline
            onClick={() => setSelectedId('add')}
          >
            <FontAwesomeIcon icon="plus" />
            <span className="d-none d-md-inline ml-2">Add Study Flag</span>
          </Button>
        }
      >
        Study Flags
      </PageTitle>

      <PartialSlicePicker />

      <Table
        data={studyFlags}
        columns={tableColumns}
        loading={loading}
        defaultSorted={[{ id: 'questionText' }]}
        filterable
        defaultFilterMethod={defaultFilterMethod}
        getTdProps={getTdProps}
      />

      {!formLoading && formData && (
        <StudyFlagForm
          data={formData}
          isOpen={!!selectedId}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </>
  );
};

export default StudyFlags;
